/**
 * The sign-up wizard's account step — presentation over `@4f/ui` primitives +
 * the injected {@link SessionStore} (DIP). Collects the credentials, display
 * name, and the mandatory 18+ attestation, then calls `session.register`, which
 * mints + persists the session (`4f:token`). The SECOND wizard step — age
 * verification — is the {@link AgeGate} the surface gate routes to next
 * (`ageOk` is still false immediately after sign-up), so it is shared with the
 * "logged-in-but-unverified" path rather than duplicated here (DRY).
 */
import { Button, Captcha, Checkbox, Form, TextInput, useT, useTheme } from '@4f/ui';
import { createSignal, type JSX, Show } from 'solid-js';
import type { SessionStore } from '../../stores/session.ts';
import { isCaptchaSatisfied, isSignupSubmittable } from './auth-model.ts';
import { captchaSitekey } from './captcha-sitekey.ts';

export interface SignupWizardProps {
  readonly session: SessionStore;
  /** An invite code from a referral link (`?ref=`), seeded into the sign-up. */
  readonly referralCode?: string;
}

export function SignupWizard(props: SignupWizardProps): JSX.Element {
  const { t } = useT();
  const { theme } = useTheme();
  const [email, setEmail] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [displayName, setDisplayName] = createSignal('');
  const [over18, setOver18] = createSignal(false);
  const [captchaToken, setCaptchaToken] = createSignal<string | null>(null);

  // Read ONCE — baked at build time. No key ⇒ the edge runs `NullCaptchaVerifier`
  // and expects no token; a key ⇒ nothing is sent until the challenge is solved,
  // because the edge refuses an empty token outright.
  const sitekey = captchaSitekey();
  const captchaOk = (): boolean => isCaptchaSatisfied(sitekey, captchaToken());

  const [resetKey, setResetKey] = createSignal(0);
  /**
   * A refused attempt has ALREADY spent the captcha token: the edge runs
   * `ensureHuman` before it looks at the credentials, and an hCaptcha token is
   * single-use. Resubmitting it fails the bot gate rather than the password, so
   * every retry after the first mistake would answer 403 with no way out but a
   * reload. Bumping the key re-arms the widget and clears the held token.
   */
  const reArmCaptchaIfRejected = (accepted: boolean): void => {
    if (!accepted) {
      setResetKey((n) => n + 1);
    }
  };

  /**
   * The store resolves `false` for every refusal the edge reports, so the
   * `.then` in `submit` covers the normal path. A THROW is different — unexpected, and
   * today only reachable through a storage write — but it would leave the widget
   * holding a spent token with no way back except a reload. Re-arm, then rethrow:
   * swallowing it here would also hide a genuine fault from GlitchTip, which is
   * the only reason it is not simply caught.
   */
  const reArmCaptchaAndRethrow = (cause: unknown): never => {
    reArmCaptchaIfRejected(false);
    throw cause;
  };

  const draft = () => ({
    email: email(),
    password: password(),
    displayName: displayName(),
    declaredOver18: over18(),
  });
  const submittable = (): boolean => isSignupSubmittable(draft()) && captchaOk();
  const submit = (): void => {
    const token = captchaToken();
    if (submittable()) {
      void props.session
        .register({
          email: email().trim(),
          password: password(),
          displayName: displayName().trim(),
          declaredOver18: over18(),
          referralCode: props.referralCode,
          ...(token ? { captchaToken: token } : {}),
        })
        .then(reArmCaptchaIfRejected)
        .catch(reArmCaptchaAndRethrow);
    }
  };

  return (
    <Form class="f4-auth__form" onSubmit={submit}>
      <TextInput
        id="signup-name"
        label={t('auth.displayName')}
        value={displayName()}
        onValue={setDisplayName}
        autocomplete="nickname"
        required
      />
      <TextInput
        id="signup-email"
        label={t('auth.email')}
        type="email"
        value={email()}
        onValue={setEmail}
        autocomplete="username"
        required
      />
      <TextInput
        id="signup-password"
        label={t('auth.password')}
        type="password"
        value={password()}
        onValue={setPassword}
        autocomplete="new-password"
        required
      />

      <Checkbox
        id="signup-over18"
        checked={over18()}
        onChange={setOver18}
        label={t('auth.over18')}
      />

      <Show when={sitekey}>
        {(key) => (
          <Captcha
            sitekey={key()}
            resetKey={resetKey()}
            theme={theme()}
            errorLabel={t('auth.captcha.unavailable')}
            onToken={setCaptchaToken}
          />
        )}
      </Show>

      <Show when={props.session.error()}>
        {(error) => (
          <p class="f4-auth__error" role="alert">
            {error().message}
          </p>
        )}
      </Show>

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={props.session.loading()}
        loadingLabel={t('auth.loading')}
        disabled={!submittable()}
      >
        {t('auth.signup.submit')}
      </Button>
    </Form>
  );
}
